import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface ListingResponse {
  status: string;
  message: string;
  listing: any;
  user_balance?: number;
}

export interface VehicleFeature {
  id: number;
  feature_name: string;
  feature_category: string;
  description: string;
}

export interface VehicleFeatures {
  [category: string]: VehicleFeature[];
}

export interface UserListing {
  table_name: string;
  id: number;
  title: string;
  price: number;
  currency: string;
  status: string;
  created_at: string;
  updated_at: string;
  expires_at?: string;
  images: string[];
  has_images: boolean;
  image_count: number;
  first_image: string | null;
  user_id?: number;
  is_owner?: boolean;
  email?: string;
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  plan_name?: string;
  plan_price?: number;
  is_featured?: boolean;
  is_suggested?: boolean;
  features?: VehicleFeature[];
}

export interface UserListingsResponse {
  status: string;
  listings: UserListing[];
  pagination: {
    page: number;
    limit: number;
    total?: number;
    pages?: number;
  };
}

export interface AllUserListings {
  vehicles: UserListing[];
  realEstate: UserListing[];
  marketplace: UserListing[];
  jobs: UserListing[];
  total: number;
}

export interface PaginatedUserListings {
  vehicles: UserListing[];
  realEstate: UserListing[];
  marketplace: UserListing[];
  jobs: UserListing[];
  total: number;
  pagination: {
    vehicles: PaginationInfo;
    realEstate: PaginationInfo;
    marketplace: PaginationInfo;
    jobs: PaginationInfo;
  };
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total?: number;
  pages?: number;
  hasMore: boolean;
  totalItems?: number;
}

export interface PaginationParams {
  page?: number;
  limit?: number;
}

export interface AllListingsPaginated {
  listings: UserListing[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ListingsService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  createVehicleListing(
    subcategory: string,
    listingData: any
  ): Observable<ListingResponse> {
    return this.http
      .post<ListingResponse>(
        `${this.apiUrl}/vehicles/${subcategory}`,
        listingData
      )
      .pipe(
        catchError((error) => {
          console.error('Error creating vehicle listing:', error);
          let errorMessage = 'Failed to create vehicle listing';

          if (error.status === 400 && error.error?.errors) {
            errorMessage = error.error.errors.join('. ');
          } else if (error.error?.message) {
            errorMessage = error.error.message;
          }

          return throwError(() => new Error(errorMessage));
        })
      );
  }

  getVehicleFeatures(subcategory: string): Observable<VehicleFeatures> {
    return this.http
      .get<VehicleFeatures>(`${this.apiUrl}/vehicles/${subcategory}/features`)
      .pipe(
        catchError((error) => {
          console.error('Error fetching vehicle features:', error);
          return of({});
        })
      );
  }

  createRealEstateListing(
    subcategory: string,
    listingData: any
  ): Observable<ListingResponse> {
    return this.http
      .post<ListingResponse>(
        `${this.apiUrl}/real-estate/${subcategory}`,
        listingData
      )
      .pipe(
        catchError((error) => {
          console.error('Error creating real estate listing:', error);
          let errorMessage = 'Failed to create real estate listing';

          if (error.status === 400 && error.error?.errors) {
            errorMessage = error.error.errors.join('. ');
          } else if (error.error?.message) {
            errorMessage = error.error.message;
          }

          return throwError(() => new Error(errorMessage));
        })
      );
  }

  createMarketplaceListing(
    subcategory: string,
    listingData: any
  ): Observable<ListingResponse> {
    return this.http
      .post<ListingResponse>(
        `${this.apiUrl}/marketplace/${subcategory}`,
        listingData
      )
      .pipe(
        catchError((error) => {
          console.error('Error creating marketplace listing:', error);
          let errorMessage = 'Failed to create marketplace listing';

          if (error.status === 400 && error.error?.errors) {
            errorMessage = error.error.errors.join('. ');
          } else if (error.error?.message) {
            errorMessage = error.error.message;
          }

          return throwError(() => new Error(errorMessage));
        })
      );
  }

  createJobsListing(
    subcategory: string,
    listingData: any
  ): Observable<ListingResponse> {
    return this.http
      .post<ListingResponse>(`${this.apiUrl}/jobs/${subcategory}`, listingData)
      .pipe(
        catchError((error) => {
          console.error('Error creating jobs listing:', error);
          let errorMessage = 'Failed to create job listing';

          if (error.status === 400 && error.error?.errors) {
            errorMessage = error.error.errors.join('. ');
          } else if (error.error?.message) {
            errorMessage = error.error.message;
          }

          return throwError(() => new Error(errorMessage));
        })
      );
  }

  createListing(
    category: string,
    subcategory: string,
    listingData: any,
    useFreeRelist: boolean = false
  ): Observable<ListingResponse> {
    if (!listingData.plan_name) {
      listingData.plan_name = 'Free';
    }
    if (!listingData.plan_price) {
      listingData.plan_price = 0;
    }
    if (!listingData.plan_duration) {
      listingData.plan_duration = 7;
    }

    if (useFreeRelist) {
      listingData.use_free_relist = true;
    }

    switch (category) {
      case 'vehicles':
        return this.createVehicleListing(subcategory, listingData);
      case 'real-estate':
        return this.createRealEstateListing(subcategory, listingData);
      case 'marketplace':
        return this.createMarketplaceListing(subcategory, listingData);
      case 'jobs':
        return this.createJobsListing(subcategory, listingData);
      default:
        return throwError(() => new Error(`Unknown category: ${category}`));
    }
  }

  getUserVehicleListings(
    page: number = 1,
    limit: number = 50
  ): Observable<UserListingsResponse> {
    return this.http
      .get<UserListingsResponse>(
        `${this.apiUrl}/vehicles/user/listings?page=${page}&limit=${limit}`
      )
      .pipe(
        map((response) => ({
          ...response,
          listings: response.listings || [],
        })),
        catchError((error) => {
          console.error('Error fetching user vehicle listings:', error);
          return of({
            status: 'error',
            listings: [],
            pagination: { page, limit },
          });
        })
      );
  }

  getUserRealEstateListings(
    page: number = 1,
    limit: number = 50
  ): Observable<UserListingsResponse> {
    return this.http
      .get<UserListingsResponse>(
        `${this.apiUrl}/real-estate/user/listings?page=${page}&limit=${limit}`
      )
      .pipe(
        map((response) => ({
          ...response,
          listings: response.listings || [],
        })),
        catchError((error) => {
          console.error('Error fetching user real estate listings:', error);
          return of({
            status: 'error',
            listings: [],
            pagination: { page, limit },
          });
        })
      );
  }

  getUserMarketplaceListings(
    page: number = 1,
    limit: number = 50
  ): Observable<UserListingsResponse> {
    return this.http
      .get<UserListingsResponse>(
        `${this.apiUrl}/marketplace/user/listings?page=${page}&limit=${limit}`
      )
      .pipe(
        map((response) => ({
          ...response,
          listings: response.listings || [],
        })),
        catchError((error) => {
          console.error('Error fetching user marketplace listings:', error);
          return of({
            status: 'error',
            listings: [],
            pagination: { page, limit },
          });
        })
      );
  }

  getUserJobsListings(
    page: number = 1,
    limit: number = 50
  ): Observable<UserListingsResponse> {
    return this.http
      .get<UserListingsResponse>(
        `${this.apiUrl}/jobs/user/listings?page=${page}&limit=${limit}`
      )
      .pipe(
        map((response) => ({
          ...response,
          listings: response.listings || [],
        })),
        catchError((error) => {
          console.error('Error fetching user job listings:', error);
          return of({
            status: 'error',
            listings: [],
            pagination: { page, limit },
          });
        })
      );
  }

  getAllUserListings(): Observable<AllUserListings> {
    return forkJoin({
      vehicles: this.getUserVehicleListings(1, 1000),
      realEstate: this.getUserRealEstateListings(1, 1000),
      marketplace: this.getUserMarketplaceListings(1, 1000),
      jobs: this.getUserJobsListings(1, 1000),
    }).pipe(
      map((responses) => {
        const vehicles = responses.vehicles.listings || [];
        const realEstate = responses.realEstate.listings || [];
        const marketplace = responses.marketplace.listings || [];
        const jobs = responses.jobs.listings || [];

        return {
          vehicles,
          realEstate,
          marketplace,
          jobs,
          total:
            vehicles.length +
            realEstate.length +
            marketplace.length +
            jobs.length,
        };
      }),
      catchError((error) => {
        console.error('Error fetching all user listings:', error);
        return of({
          vehicles: [],
          realEstate: [],
          marketplace: [],
          jobs: [],
          total: 0,
        });
      })
    );
  }

  getAllUserListingsPaginated(
    params: PaginationParams = {}
  ): Observable<AllListingsPaginated> {
    const { page = 1, limit = 20 } = params;

    return this.getAllUserListings().pipe(
      map((allData) => {
        const allListings = [
          ...allData.vehicles,
          ...allData.realEstate,
          ...allData.marketplace,
          ...allData.jobs,
        ].sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );

        const totalCount = allListings.length;
        const totalPages = Math.ceil(totalCount / limit);
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        const paginatedListings = allListings.slice(startIndex, endIndex);

        return {
          listings: paginatedListings,
          totalCount,
          currentPage: page,
          totalPages,
          hasNextPage: page < totalPages,
          hasPreviousPage: page > 1,
        };
      })
    );
  }

  getCategoryListingsPaginated(
    params: PaginationParams = {}
  ): Observable<PaginatedUserListings> {
    const { page = 1, limit = 20 } = params;

    return forkJoin({
      vehicles: this.getUserVehicleListings(page, limit),
      realEstate: this.getUserRealEstateListings(page, limit),
      marketplace: this.getUserMarketplaceListings(page, limit),
      jobs: this.getUserJobsListings(page, limit),
    }).pipe(
      map((responses) => {
        const result: PaginatedUserListings = {
          vehicles: responses.vehicles.listings || [],
          realEstate: responses.realEstate.listings || [],
          marketplace: responses.marketplace.listings || [],
          jobs: responses.jobs.listings || [],
          total: 0,
          pagination: {
            vehicles: {
              page: responses.vehicles.pagination?.page || page,
              limit: responses.vehicles.pagination?.limit || limit,
              total:
                responses.vehicles.pagination?.total ||
                (responses.vehicles.listings || []).length,
              pages:
                responses.vehicles.pagination?.pages ||
                Math.ceil((responses.vehicles.listings || []).length / limit),
              hasMore: (responses.vehicles.listings || []).length === limit,
              totalItems: (responses.vehicles.listings || []).length,
            },
            realEstate: {
              page: responses.realEstate.pagination?.page || page,
              limit: responses.realEstate.pagination?.limit || limit,
              total:
                responses.realEstate.pagination?.total ||
                (responses.realEstate.listings || []).length,
              pages:
                responses.realEstate.pagination?.pages ||
                Math.ceil((responses.realEstate.listings || []).length / limit),
              hasMore: (responses.realEstate.listings || []).length === limit,
              totalItems: (responses.realEstate.listings || []).length,
            },
            marketplace: {
              page: responses.marketplace.pagination?.page || page,
              limit: responses.marketplace.pagination?.limit || limit,
              total:
                responses.marketplace.pagination?.total ||
                (responses.marketplace.listings || []).length,
              pages:
                responses.marketplace.pagination?.pages ||
                Math.ceil(
                  (responses.marketplace.listings || []).length / limit
                ),
              hasMore: (responses.marketplace.listings || []).length === limit,
              totalItems: (responses.marketplace.listings || []).length,
            },
            jobs: {
              page: responses.jobs.pagination?.page || page,
              limit: responses.jobs.pagination?.limit || limit,
              total:
                responses.jobs.pagination?.total ||
                (responses.jobs.listings || []).length,
              pages:
                responses.jobs.pagination?.pages ||
                Math.ceil((responses.jobs.listings || []).length / limit),
              hasMore: (responses.jobs.listings || []).length === limit,
              totalItems: (responses.jobs.listings || []).length,
            },
          },
        };

        result.total =
          result.vehicles.length +
          result.realEstate.length +
          result.marketplace.length +
          result.jobs.length;

        return result;
      }),
      catchError((error) => {
        console.error('Error fetching paginated user listings:', error);
        return of({
          vehicles: [],
          realEstate: [],
          marketplace: [],
          jobs: [],
          total: 0,
          pagination: {
            vehicles: { page, limit, hasMore: false, totalItems: 0 },
            realEstate: { page, limit, hasMore: false, totalItems: 0 },
            marketplace: { page, limit, hasMore: false, totalItems: 0 },
            jobs: { page, limit, hasMore: false, totalItems: 0 },
          },
        });
      })
    );
  }

  loadMoreUserListings(
    category: string,
    page: number,
    limit: number = 20
  ): Observable<UserListingsResponse> {
    switch (category) {
      case 'vehicles':
        return this.getUserVehicleListings(page, limit);
      case 'realEstate':
        return this.getUserRealEstateListings(page, limit);
      case 'marketplace':
        return this.getUserMarketplaceListings(page, limit);
      case 'jobs':
        return this.getUserJobsListings(page, limit);
      default:
        return throwError(() => new Error(`Unknown category: ${category}`));
    }
  }

  getAllUserListingsSorted(): Observable<UserListing[]> {
    return this.getAllUserListings().pipe(
      map((data) => {
        const allListings = [
          ...data.vehicles,
          ...data.realEstate,
          ...data.marketplace,
          ...data.jobs,
        ];
        return allListings.sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      })
    );
  }

  getCategoryFromSubcategory(subcategory: string): string {
    const subcategoryToCategoryMap: { [key: string]: string } = {
      automobile: 'vehicles',
      motorcycle: 'vehicles',
      'commercial-vehicles': 'vehicles',
      oldtimer: 'vehicles',
      nautical: 'vehicles',
      'spare-parts': 'vehicles',
      'campers-caravans': 'vehicles',
      'crashed-cars': 'vehicles',
      'construction': 'vehicles',
      'agriculture': 'vehicles',
      'house-sale': 'real-estate',
      'house-rent': 'real-estate',
      'apartments-sale': 'real-estate',
      'apartments-rent': 'real-estate',
      'land-sale': 'real-estate',
      'land-rent': 'real-estate',
      'business-office-sale': 'real-estate',
      'business-office-rent': 'real-estate',
      'garage-sale': 'real-estate',
      'garage-rent': 'real-estate',
      'luxury-real-estate-sale': 'real-estate',
      'luxury-real-estate-rent': 'real-estate',
      'room-rent': 'real-estate',
      'looking-for-roommate': 'real-estate',
      home: 'marketplace',
      electronics: 'marketplace',
      sport: 'marketplace',
      children: 'marketplace',
      'head-to-toe': 'marketplace',
      'machines-tools': 'marketplace',
      media: 'marketplace',
      'musical-instruments': 'marketplace',
      literature: 'marketplace',
      pets: 'marketplace',
      other: 'marketplace',
      'giving-away': 'marketplace',
      'home-maintenance': 'jobs',
      'personal-care-wellness': 'jobs',
      'events-entertainment': 'jobs',
      'education-training': 'jobs',
      'transport-delivery': 'jobs',
      professional: 'jobs',
    };
    return subcategoryToCategoryMap[subcategory] || 'unknown';
  }

  getCategoryFromTableName(tableName: string): string {
    let cleanTableName = tableName;
    if (tableName.startsWith('attributes_')) {
      cleanTableName = tableName.replace('attributes_', '');
    }

    const categoryMap: { [key: string]: string } = {
      automobile: 'Vehicles',
      motorcycle: 'Vehicles',
      commercial_vehicles: 'Vehicles',
      oldtimer: 'Vehicles',
      nautical: 'Vehicles',
      spare_parts: 'Vehicles',
      campers_caravans: 'Vehicles',
      crashed_cars: 'Vehicles',
      construction: 'Vehicles',
      agriculture: 'Vehicles',
      house_sale: 'Real Estate',
      house_rent: 'Real Estate',
      apartments_sale: 'Real Estate',
      apartments_rent: 'Real Estate',
      land_sale: 'Real Estate',
      land_rent: 'Real Estate',
      business_office_sale: 'Real Estate',
      business_office_rent: 'Real Estate',
      garage_sale: 'Real Estate',
      garage_rent: 'Real Estate',
      luxury_real_estate_sale: 'Real Estate',
      luxury_real_estate_rent: 'Real Estate',
      room_rent: 'Real Estate',
      roommate: 'Real Estate',
      looking_for_roommate: 'Real Estate',
      home: 'Marketplace',
      electronics: 'Marketplace',
      sport: 'Marketplace',
      children: 'Marketplace',
      head_to_toe: 'Marketplace',
      machines_tools: 'Marketplace',
      media: 'Marketplace',
      musical_instruments: 'Marketplace',
      literature: 'Marketplace',
      pets: 'Marketplace',
      other: 'Marketplace',
      giving_away: 'Marketplace',
      home_maintenance: 'Jobs',
      personal_care_wellness: 'Jobs',
      events_entertainment: 'Jobs',
      education_training: 'Jobs',
      transport_delivery: 'Jobs',
      professional: 'Jobs',
    };
    return categoryMap[cleanTableName] || 'Unknown';
  }

  getSubcategoryFromTableName(tableName: string): string {
    let cleanTableName = tableName;
    if (tableName.startsWith('attributes_')) {
      cleanTableName = tableName.replace('attributes_', '');
    }

    const subcategoryMap: { [key: string]: string } = {
      automobile: 'automobile',
      motorcycle: 'motorcycle',
      commercial_vehicles: 'commercial-vehicles',
      oldtimer: 'oldtimer',
      nautical: 'nautical',
      spare_parts: 'spare-parts',
      campers_caravans: 'campers-caravans',
      crashed_cars: 'crashed-cars',
      construction: 'construction',
      agriculture: 'agriculture',
      house_sale: 'house-sale',
      house_rent: 'house-rent',
      apartments_sale: 'apartments-sale',
      apartments_rent: 'apartments-rent',
      land_sale: 'land-sale',
      land_rent: 'land-rent',
      business_office_sale: 'business-office-sale',
      business_office_rent: 'business-office-rent',
      garage_sale: 'garage-sale',
      garage_rent: 'garage-rent',
      luxury_real_estate_sale: 'luxury-real-estate-sale',
      luxury_real_estate_rent: 'luxury-real-estate-rent',
      room_rent: 'room-rent',
      roommate: 'looking-for-roommate',
      looking_for_roommate: 'looking-for-roommate',
      home: 'home',
      electronics: 'electronics',
      sport: 'sport',
      children: 'children',
      head_to_toe: 'head-to-toe',
      machines_tools: 'machines-tools',
      media: 'media',
      musical_instruments: 'musical-instruments',
      literature: 'literature',
      pets: 'pets',
      other: 'other',
      giving_away: 'giving-away',
      home_maintenance: 'home-maintenance',
      personal_care_wellness: 'personal-care-wellness',
      events_entertainment: 'events-entertainment',
      education_training: 'education-training',
      transport_delivery: 'transport-delivery',
      professional: 'professional',
    };
    return subcategoryMap[cleanTableName] || cleanTableName;
  }

  deleteListing(
    category: string,
    subcategory: string,
    listingId: number
  ): Observable<{ message: string }> {
    return this.http
      .delete<{ message: string }>(
        `${this.apiUrl}/${category}/${subcategory}/${listingId}`
      )
      .pipe(
        catchError((error) => {
          console.error(`Error deleting ${category} listing:`, error);
          let errorMessage = 'Failed to delete listing';
          if (error.status === 403) {
            errorMessage =
              'Access denied. You can only delete your own listings.';
          } else if (error.status === 404) {
            errorMessage = 'Listing not found';
          } else if (error.error && error.error.message) {
            errorMessage = error.error.message;
          }
          return throwError(() => new Error(errorMessage));
        })
      );
  }

  updateListing(
    category: string,
    subcategory: string,
    listingId: number,
    updateData: any
  ): Observable<{ message: string; listing?: any }> {
    return this.http
      .put<{ message: string; listing?: any }>(
        `${this.apiUrl}/${category}/${subcategory}/${listingId}`,
        updateData
      )
      .pipe(
        catchError((error) => {
          console.error(`Error updating ${category} listing:`, error);
          let errorMessage = 'Failed to update listing';
          if (error.status === 403) {
            errorMessage =
              'Access denied. You can only update your own listings.';
          } else if (error.status === 404) {
            errorMessage = 'Listing not found';
          } else if (error.error && error.error.message) {
            errorMessage = error.error.message;
          }
          return throwError(() => new Error(errorMessage));
        })
      );
  }

  getCategoryAndSubcategoryFromTableName(tableName: string): {
    category: string;
    subcategory: string;
  } {
    if (!tableName.startsWith('attributes_')) {
      throw new Error('Invalid table name format');
    }

    const tableCategory = tableName.replace('attributes_', '');

    if (
      [
        'automobile',
        'motorcycle',
        'commercial_vehicles',
        'oldtimer',
        'nautical',
        'spare_parts',
        'campers_caravans',
        'crashed_cars',
        'construction',
        'agriculture'
      ].includes(tableCategory)
    ) {
      let subcategory = tableCategory;
      if (tableCategory === 'spare_parts') subcategory = 'spare-parts';
      if (tableCategory === 'commercial_vehicles')
        subcategory = 'commercial-vehicles';
      if (tableCategory === 'campers_caravans')
        subcategory = 'campers-caravans';
      if (tableCategory === 'crashed_cars') subcategory = 'crashed-cars';
      if (tableCategory === 'construction') subcategory = 'construction';
      if (tableCategory === 'agriculture') subcategory = 'agriculture';
      return { category: 'vehicles', subcategory };
    }

    if (
      [
        'house_sale',
        'house_rent',
        'apartments_sale',
        'apartments_rent',
        'land_sale',
        'land_rent',
        'business_office_sale',
        'business_office_rent',
        'garage_sale',
        'garage_rent',
        'luxury_real_estate_sale',
        'luxury_real_estate_rent',
        'room_rent',
        'roommate',
        'looking_for_roommate',
      ].includes(tableCategory)
    ) {
      let subcategory = tableCategory.replace(/_/g, '-');
      if (tableCategory === 'roommate') subcategory = 'looking-for-roommate';
      return { category: 'real-estate', subcategory };
    }

    if (
      [
        'professional',
        'home_maintenance',
        'personal_care_wellness',
        'events_entertainment',
        'education_training',
        'transport_delivery',
      ].includes(tableCategory)
    ) {
      let subcategory = tableCategory.replace(/_/g, '-');
      return { category: 'jobs', subcategory };
    }

    let subcategory = tableCategory.replace(/_/g, '-');
    return { category: 'marketplace', subcategory };
  }

  deleteUserListing(listing: UserListing): Observable<{ message: string }> {
    const { category, subcategory } =
      this.getCategoryAndSubcategoryFromTableName(listing.table_name);
    return this.deleteListing(category, subcategory, listing.id);
  }

  updateUserListing(
    listing: UserListing,
    updateData: any
  ): Observable<{ message: string; listing?: any }> {
    const { category, subcategory } =
      this.getCategoryAndSubcategoryFromTableName(listing.table_name);
    return this.updateListing(category, subcategory, listing.id, updateData);
  }

  getListingDetails(
    listingId: string,
    subcategory: string
  ): Observable<UserListing> {
    const category = this.getCategoryFromSubcategory(subcategory);

    if (category === 'unknown') {
      return throwError(() => new Error(`Unknown subcategory: ${subcategory}`));
    }

    const apiUrl = `${this.apiUrl}/${category}/${subcategory}/${listingId}`;

    return this.http.get<any>(apiUrl).pipe(
      map((response) => {
        const listingData = response.listing || response;

        if (
          this.isVehicleTableName(listingData.table_name) &&
          !listingData.features
        ) {
          listingData.features = [];
        }

        if (!listingData.table_name) {
          const tableMap: { [key: string]: string } = {
            automobile: 'attributes_automobile',
            motorcycle: 'attributes_motorcycle',
            'commercial-vehicles': 'attributes_commercial_vehicles',
            oldtimer: 'attributes_oldtimer',
            nautical: 'attributes_nautical',
            'spare-parts': 'attributes_spare_parts',
            'campers-caravans': 'attributes_campers_caravans',
            'crashed-cars': 'attributes_crashed_cars',
            'construction': 'attributes_construction',
            'agriculture': 'attributes_agriculture',
            'house-sale': 'attributes_house_sale',
            'house-rent': 'attributes_house_rent',
            'apartments-sale': 'attributes_apartments_sale',
            'apartments-rent': 'attributes_apartments_rent',
            'land-sale': 'attributes_land_sale',
            'land-rent': 'attributes_land_rent',
            'business-office-sale': 'attributes_business_office_sale',
            'business-office-rent': 'attributes_business_office_rent',
            'garage-sale': 'attributes_garage_sale',
            'garage-rent': 'attributes_garage_rent',
            'luxury-real-estate-sale': 'attributes_luxury_real_estate_sale',
            'luxury-real-estate-rent': 'attributes_luxury_real_estate_rent',
            'room-rent': 'attributes_room_rent',
            'looking-for-roommate': 'attributes_roommate',
            home: 'attributes_home',
            electronics: 'attributes_electronics',
            sport: 'attributes_sport',
            children: 'attributes_children',
            'head-to-toe': 'attributes_head_to_toe',
            'machines-tools': 'attributes_machines_tools',
            media: 'attributes_media',
            'musical-instruments': 'attributes_musical_instruments',
            literature: 'attributes_literature',
            pets: 'attributes_pets',
            other: 'attributes_other',
            'giving-away': 'attributes_giving_away',
            'home-maintenance': 'attributes_home_maintenance',
            'personal-care-wellness': 'attributes_personal_care_wellness',
            'events-entertainment': 'attributes_events_entertainment',
            'education-training': 'attributes_education_training',
            'transport-delivery': 'attributes_transport_delivery',
            professional: 'attributes_professional',
          };
          listingData.table_name =
            tableMap[subcategory] ||
            `attributes_${subcategory.replace(/-/g, '_')}`;
        }
        return listingData as UserListing;
      }),
      catchError((error) => {
        console.error('Error fetching listing details:', error);
        let errorMessage = 'Failed to fetch listing details';
        if (error.status === 404) {
          errorMessage = 'Listing not found';
        } else if (error.error?.message) {
          errorMessage = error.error.message;
        }
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  private isVehicleTableName(tableName: string): boolean {
    if (!tableName) return false;

    const vehicleTableNames = [
      'attributes_automobile',
      'attributes_motorcycle',
      'attributes_commercial_vehicles',
      'attributes_oldtimer',
      'attributes_nautical',
      'attributes_spare_parts',
      'attributes_campers_caravans',
      'attributes_crashed_cars',
      'attributes_construction',
      'attributes_agriculture'
    ];

    return vehicleTableNames.includes(tableName);
  }

  getPlanBadge(listing: UserListing): string {
    if (!listing.plan_name) return '';

    const planBadges: { [key: string]: string } = {
      Free: '',
      Starter: '🌟',
      Pro: '⭐',
      Elite: '💎',
    };

    return planBadges[listing.plan_name] || '';
  }

  isPremiumListing(listing: UserListing): boolean {
    return listing.plan_name !== 'Free' && listing.plan_name !== undefined;
  }

  getPlanColor(planName: string): string {
    const planColors: { [key: string]: string } = {
      Free: '#6c757d',
      Starter: '#3498db',
      Pro: '#9b59b6',
      Elite: '#e74c3c',
    };

    return planColors[planName] || '#6c757d';
  }
}
